import { ContractArea } from "../../types";
import { RemoteData } from "@nvdb-rapport/shared/src/utils/remoteData";
import { ErrorData } from "../../update/ui";
import { Maybe, Nothing } from "purify-ts/Maybe";
import { VegSystem } from "@nvdb-rapport/shared/src/utils/vegfilter";
import { Either, Right } from "purify-ts/Either";
import { formatDate } from "@nvdb-rapport/shared/src/components/DatePicker";
import { GetInterface, Codec } from "purify-ts";
import * as R from "remeda";
import { VegobjektTypeList, VegobjektType } from "../../utils/vegobjekt";
import {
  Field,
  initialField,
} from "@nvdb-rapport/shared/src/components/formTypes";
import {
  Fylke,
  Omraader,
  Kommune,
} from "@nvdb-rapport/shared/src/components/AreaSelect/types";
import { ReadVersionOption } from "../FormComponents/ReadVersionSelect";

export type GeneriskModelProps = {
  driftskontraktState: GeneriskModel;
};
export type GeneriskActionProps = {
  driftskontraktDispatch: React.Dispatch<GeneriskAction>;
};

export const CustomReportData = Codec.interface({
  omit: VegobjektTypeList,
  add: VegobjektTypeList,
});

export type CustomReportData = GetInterface<typeof CustomReportData>;

interface GeneriskRapportType {
  beskrivelse: string;
  id: number;
  navn: string;
  feature: string;
}

export interface GeneriskModel {
  contractAreas: RemoteData<ErrorData, ContractArea[]>;
  vegobjektTilvalg: RemoteData<ErrorData, VegobjektType[]>;
  vegobjektDefault: RemoteData<ErrorData, VegobjektType[]>;
  selectedVegobjekt: Maybe<VegobjektType>;
  selectedSecondaryVegobjekt: Maybe<VegobjektType>;
  selectedReportType: Maybe<GeneriskRapportType>;
  selectedContractArea: Maybe<ContractArea>;
  reportTypes: GeneriskRapportType[];
  omraader: RemoteData<Error, Omraader>;
  selectedFylker: Field<Fylke[]>;
  selectedKommuner: Field<Kommune[]>;
  date: Either<string, string>;
  reportTypeInput: string;
  customReport: CustomReportData;
  vegsystemInput: string;
  vegsystem: Either<string, VegSystem>;
  showValidationErrors: boolean;
  selectedReadVersion: ReadVersionOption | null;
}

export const initialGeneriskModel: GeneriskModel = {
  selectedReportType: Nothing,
  contractAreas: { type: "NOT_ASKED" },
  vegobjektTilvalg: { type: "NOT_ASKED" },
  vegobjektDefault: { type: "NOT_ASKED" },
  selectedVegobjekt: Nothing,
  selectedSecondaryVegobjekt: Nothing,
  reportTypes: [],
  omraader: { type: "NOT_ASKED" },
  customReport: { omit: [], add: [] },
  vegsystemInput: "",
  vegsystem: Right([]),
  selectedFylker: initialField([]),
  selectedKommuner: initialField([]),
  reportTypeInput: "",
  selectedContractArea: Nothing,
  date: Right(formatDate(new Date())),
  showValidationErrors: false,
  selectedReadVersion: { label: "v4", value: true },
};

type Reset = {
  type: "Reset";
};

type ResetTilvalg = {
  type: "ResetTilvalg";
};

type UpdateVegsystem = {
  type: "UpdateVegsystem";
  payload: string;
};

type SetVegobjektTilvalg = {
  type: "SetVegobjektTilvalg";
  payload: RemoteData<ErrorData, VegobjektType[]>;
};

type SetVegobjectDefault = {
  type: "SetVegobjectDefault";
  payload: RemoteData<ErrorData, VegobjektType[]>;
};

type SelectOneVegobjekt = {
  type: "SelectOneVegobjekt";
  payload: Maybe<VegobjektType>;
};

type SelectSecondaryVegobjekt = {
  type: "SelectSecondaryVegobjekt";
  payload: Maybe<VegobjektType>;
};

type GotReportTypes = {
  type: "GotReportTypes";
  payload: RemoteData<ErrorData, GeneriskRapportType[]>;
};

type SetDate = {
  type: "SetDate";
  payload: Either<string, string>;
};

type UpdateCustomAdditions = {
  type: "UpdateCustomAdditions";
  payload: VegobjektType[];
};

type UpdateCustomRemovals = {
  type: "UpdateCustomRemovals";
  payload: VegobjektType[];
};

type ToggleSelectedFylke = {
  type: "ToggleSelectedFylke";
  payload: Field<Fylke[]>;
};

type SelectContractArea = {
  type: "SelectContractArea";
  payload: Maybe<ContractArea>;
};

type SetReadVersion = {
  type: "SetReadVersion";
  payload: ReadVersionOption | null;
};

export function toggleSelectedFylkeAction(
  fylke: Field<Fylke[]>
): ToggleSelectedFylke {
  return {
    type: "ToggleSelectedFylke",
    payload: fylke,
  };
}

type UpdateSelectedKommune = {
  type: "UpdateSelectedKommune";
  payload: Field<Fylke[]>;
};

export function updateSelectedKommuneAction(
  kommune: Field<Kommune[]>
): UpdateSelectedKommune {
  return {
    type: "UpdateSelectedKommune",
    payload: kommune,
  };
}

type SetOmraader = {
  type: "SetOmraader";
  payload: RemoteData<Error, Omraader>;
};

export function setOmraaderAction(
  data: RemoteData<Error, Omraader>
): SetOmraader {
  return {
    type: "SetOmraader",
    payload: data,
  };
}

type GotContractAreas = {
  type: "GotContractAreas";
  payload: RemoteData<ErrorData, ContractArea[]>;
};

type SelectReportType = {
  type: "SelectReportType";
  payload: Maybe<GeneriskRapportType>;
};

type ShowValidationErrors = {
  type: "ShowValidationErrors";
  payload: boolean;
};

export type GeneriskAction =
  | SetDate
  | Reset
  | UpdateVegsystem
  | SetVegobjektTilvalg
  | SetVegobjectDefault
  | SelectOneVegobjekt
  | SelectSecondaryVegobjekt
  | GotReportTypes
  | UpdateCustomAdditions
  | UpdateCustomRemovals
  | ToggleSelectedFylke
  | SetOmraader
  | UpdateSelectedKommune
  | SelectContractArea
  | ResetTilvalg
  | SelectReportType
  | GotContractAreas
  | ShowValidationErrors
  | SetReadVersion;

export function generiskReducer(
  model: GeneriskModel,
  action: GeneriskAction
): GeneriskModel {
  switch (action.type) {
    case "SelectReportType":
      return R.pipe(model, R.set("selectedReportType", action.payload));
    case "SetDate":
      return { ...model, date: action.payload };
    case "GotContractAreas":
      return { ...model, contractAreas: action.payload };
    case "Reset":
      return {
        ...initialGeneriskModel,
      };
    case "SelectContractArea":
      return R.pipe(
        model,
        R.set("selectedContractArea", action.payload),
        R.set("showValidationErrors", false)
      );
    case "UpdateVegsystem":
      return R.pipe(
        model,
        R.set("vegsystemInput", action.payload),
        R.set("vegsystem", VegSystem.decode(action.payload.split(",")))
      );
    case "SetVegobjektTilvalg":
      return R.pipe(model, R.set("vegobjektTilvalg", action.payload));

    case "SetVegobjectDefault":
      return R.pipe(model, R.set("vegobjektDefault", action.payload));

    case "SelectOneVegobjekt":
      return R.pipe(model, R.set("selectedVegobjekt", action.payload));
    case "SelectSecondaryVegobjekt":
      return R.pipe(model, R.set("selectedSecondaryVegobjekt", action.payload));
    case "GotReportTypes":
      if (action.payload.type === "SUCCESS") {
        return { ...model, reportTypes: action.payload.data };
      } else {
        return model;
      }

    case "UpdateCustomAdditions":
      return R.pipe(
        model,
        R.set(
          "customReport",
          R.pipe(model.customReport, R.set("add", action.payload))
        )
      );
    case "UpdateCustomRemovals":
      return R.pipe(
        model,
        R.set(
          "customReport",
          R.pipe(model.customReport, R.set("omit", action.payload))
        )
      );
    case "ResetTilvalg":
      if (model.vegobjektTilvalg.type === "SUCCESS") {
        return R.pipe(
          model,
          R.set(
            "customReport",
            R.pipe(
              model.customReport,
              R.set("omit", model.vegobjektTilvalg.data ?? []),
              R.set("add", [])
            )
          )
        );
      } else return model;

    case "ToggleSelectedFylke":
      return R.pipe(
        model,
        R.set("selectedFylker", action.payload),
        R.set("showValidationErrors", false)
      );
    case "UpdateSelectedKommune":
      return R.pipe(
        model,
        R.set("selectedKommuner", action.payload),
        R.set("showValidationErrors", false)
      );
    case "SetOmraader":
      return R.pipe(model, R.set("omraader", action.payload));

    case "ShowValidationErrors":
      return R.pipe(model, R.set("showValidationErrors", action.payload));

    case "SetReadVersion":
      return R.pipe(model, R.set("selectedReadVersion", action.payload));
  }
}
